import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_URL } from "../config";

interface ServiceOption {
  id: number;
  name: string;
  type: 'visa_pack' | 'trip_booking' | 'rental_car';
  price?: number;
  currency?: string;
  code?: string;
}

interface InvoiceLineItem {
  key: string;
  service_type: 'visa_pack' | 'trip_booking' | 'rental_car' | '';
  service_id: string;
  service_code: string;
  description: string;
  unit: string;
  quantity: string;
  unit_price: string;
}

const emptyLine = (): InvoiceLineItem => ({
  key: Math.random().toString(36).slice(2),
  service_type: "",
  service_id: "",
  service_code: "",
  description: "",
  unit: "service",
  quantity: "1",
  unit_price: "",
});

const CURRENCIES: { code: string; label: string }[] = [
  { code: "OMR", label: "OMR - Omani Rial" },
  { code: "AED", label: "AED - UAE Dirham" },
  { code: "SAR", label: "SAR - Saudi Riyal" },
  { code: "QAR", label: "QAR - Qatari Riyal" },
  { code: "KWD", label: "KWD - Kuwaiti Dinar" },
  { code: "BHD", label: "BHD - Bahraini Dinar" },
  { code: "USD", label: "USD - US Dollar" },
  { code: "EUR", label: "EUR - Euro" },
  { code: "GBP", label: "GBP - British Pound" },
  { code: "JOD", label: "JOD - Jordanian Dinar" },
  { code: "EGP", label: "EGP - Egyptian Pound" },
  { code: "LBP", label: "LBP - Lebanese Pound" },
  { code: "IQD", label: "IQD - Iraqi Dinar" },
  { code: "YER", label: "YER - Yemeni Rial" },
  { code: "INR", label: "INR - Indian Rupee" },
  { code: "PKR", label: "PKR - Pakistani Rupee" },
  { code: "BDT", label: "BDT - Bangladeshi Taka" },
  { code: "LKR", label: "LKR - Sri Lankan Rupee" },
  { code: "NPR", label: "NPR - Nepalese Rupee" },
  { code: "PHP", label: "PHP - Philippine Peso" },
  { code: "CNY", label: "CNY - Chinese Yuan" },
  { code: "JPY", label: "JPY - Japanese Yen" },
  { code: "KRW", label: "KRW - South Korean Won" },
  { code: "THB", label: "THB - Thai Baht" },
  { code: "MYR", label: "MYR - Malaysian Ringgit" },
  { code: "SGD", label: "SGD - Singapore Dollar" },
  { code: "IDR", label: "IDR - Indonesian Rupiah" },
  { code: "VND", label: "VND - Vietnamese Dong" },
  { code: "TRY", label: "TRY - Turkish Lira" },
  { code: "RUB", label: "RUB - Russian Ruble" },
  { code: "ZAR", label: "ZAR - South African Rand" },
  { code: "NGN", label: "NGN - Nigerian Naira" },
  { code: "KES", label: "KES - Kenyan Shilling" },
  { code: "MAD", label: "MAD - Moroccan Dirham" },
  { code: "TND", label: "TND - Tunisian Dinar" },
  { code: "DZD", label: "DZD - Algerian Dinar" },
  { code: "CAD", label: "CAD - Canadian Dollar" },
  { code: "AUD", label: "AUD - Australian Dollar" },
  { code: "NZD", label: "NZD - New Zealand Dollar" },
  { code: "CHF", label: "CHF - Swiss Franc" },
  { code: "SEK", label: "SEK - Swedish Krona" },
  { code: "NOK", label: "NOK - Norwegian Krone" },
  { code: "DKK", label: "DKK - Danish Krone" },
  { code: "PLN", label: "PLN - Polish Zloty" },
  { code: "CZK", label: "CZK - Czech Koruna" },
  { code: "HUF", label: "HUF - Hungarian Forint" },
  { code: "BRL", label: "BRL - Brazilian Real" },
  { code: "MXN", label: "MXN - Mexican Peso" },
];

const colors = {
  primary: "#294b5a",
  primaryHover: "#1a3641",
  text: "#1e293b",
  textMuted: "#5f6b76",
  textFaint: "#94a3b8",
  border: "#dbe0e4",
  borderLight: "#e5e7eb",
  bgPage: "#f4f6f8",
  bgSubtle: "#f8fafc",
  danger: "#ef4444",
  cancelBg: "#d3be76",
  cancelHover: "#c2ad65",
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: `1px solid ${colors.border}`,
  borderRadius: '8px',
  fontSize: '13px',
  backgroundColor: colors.bgSubtle,
  boxSizing: 'border-box',
  outline: 'none',
};

const labelStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: 600,
  color: colors.textMuted,
  display: 'block',
  marginBottom: '6px'
};

const AddInvoice = () => {
  const navigate = useNavigate();

  const token = localStorage.getItem("token_admin_silverjarz");

  const config_token = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    customer_address: "",
    currency: "OMR",
    tax_rate: "0",
    due_date: "",
    notes: ""
  });

  const [items, setItems] = useState<InvoiceLineItem[]>([emptyLine()]);
  const [services, setServices] = useState<ServiceOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingServices, setIsLoadingServices] = useState(false);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setIsLoadingServices(true);

        // Récupérer tous les packs actifs
        const response = await axios.get(`${API_URL}/visa-packs`, {
          params: { status: 'active', per_page: 100 },
          ...config_token
        });
        
        // La réponse est paginée, les données sont dans data.data
        const packs = response.data?.data?.data || response.data?.data || [];
        
        const visaPacks: ServiceOption[] = packs.map((pack: any) => ({
          id: pack.id,
          name: pack.name_en || pack.name || `Visa Pack #${pack.id}`,
          type: "visa_pack" as const,
          price: pack.price !== null && pack.price !== undefined ? Number(pack.price) : undefined,
          currency: pack.currency || 'OMR',
          code: `VP-${pack.id}`,
        }));

        // Données mockées pour les trips et rental cars
        const mockTripAndCarServices: ServiceOption[] = [
          { id: 101, name: "Paris City Tour - 3 Days", type: "trip_booking", price: 450, code: "TB-101" },
          { id: 102, name: "Bali Adventure Package", type: "trip_booking", price: 750, code: "TB-102" },
          { id: 103, name: "Tokyo Cultural Experience", type: "trip_booking", price: 600, code: "TB-103" },
          { id: 201, name: "Toyota Corolla - 7 Days", type: "rental_car", price: 280, code: "RC-201" },
          { id: 202, name: "BMW X5 - Luxury SUV", type: "rental_car", price: 650, code: "RC-202" },
          { id: 203, name: "Honda Civic - Compact", type: "rental_car", price: 220, code: "RC-203" },
        ];

        setServices([...visaPacks, ...mockTripAndCarServices]);
        
        if (visaPacks.length === 0) {
          toast.info("No active visa packs found. Please create some first.");
        }
      } catch (error) {
        console.error("Error fetching services:", error);
        toast.error("Failed to load services");
        
        // Fallback avec des données mockées
        const fallbackServices: ServiceOption[] = [
          { id: 1, name: "Oman Tourist Visa - 10 Days", type: "visa_pack", price: 45, currency: "OMR", code: "VP-1" },
          { id: 2, name: "UAE Tourist Visa - 30 Days", type: "visa_pack", price: 120, currency: "AED", code: "VP-2" },
          { id: 101, name: "Paris City Tour - 3 Days", type: "trip_booking", price: 450, code: "TB-101" },
          { id: 102, name: "Bali Adventure Package", type: "trip_booking", price: 750, code: "TB-102" },
          { id: 201, name: "Toyota Corolla - 7 Days", type: "rental_car", price: 280, code: "RC-201" },
        ];
        setServices(fallbackServices);
      } finally {
        setIsLoadingServices(false);
      }
    };

    fetchServices();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addLine = () => setItems(prev => [...prev, emptyLine()]);

  const removeLine = (key: string) => {
    setItems(prev => (prev.length > 1 ? prev.filter(i => i.key !== key) : prev));
  };

  const updateLine = (key: string, field: keyof InvoiceLineItem, value: string) => {
    setItems(prev => {
      const isFirstLine = prev.findIndex(l => l.key === key) === 0;

      return prev.map(line => {
        if (line.key !== key) return line;
        const next = { ...line, [field]: value };

        if (field === "service_type") {
          next.service_id = "";
          next.service_code = "";
          next.description = "";
          next.unit_price = "";
        }

        if (field === "service_id" && value) {
          const selected = services.find(s => String(s.id) === value);
          if (selected) {
            next.description = selected.name;
            next.service_code = selected.code || "";
            if (selected.price !== undefined) next.unit_price = String(selected.price);

            if (isFirstLine && selected.currency) {
              setFormData(fd => ({ ...fd, currency: selected.currency! }));
            }
          }
        }

        return next;
      });
    });
  };

  const filteredServicesFor = (type: string) => services.filter(s => s.type === type);

  const lineAmount = (line: InvoiceLineItem) => {
    const qty = parseFloat(line.quantity || "0");
    const price = parseFloat(line.unit_price || "0");
    return qty > 0 && price >= 0 ? qty * price : 0;
  };

  const subtotal = items.reduce((sum, line) => sum + lineAmount(line), 0);
  const taxRateNum = parseFloat(formData.tax_rate || "0");
  const taxAmount = subtotal * taxRateNum / 100;
  const total = subtotal + taxAmount;

  const validateForm = () => {
    if (!formData.customer_name.trim()) {
      toast.error("Customer name is required");
      return false;
    }
    if (!formData.customer_email.trim()) {
      toast.error("Customer email is required");
      return false;
    }
    if (!formData.due_date) {
      toast.error("Due date is required");
      return false;
    }
    if (items.length === 0) {
      toast.error("Add at least one invoice line");
      return false;
    }
    for (const line of items) {
      if (!line.service_type) {
        toast.error("Every line needs a service type");
        return false;
      }
      if (!line.service_id) {
        toast.error("Every line needs a service selected");
        return false;
      }
      if (!line.quantity || parseFloat(line.quantity) <= 0) {
        toast.error("Every line needs a valid quantity");
        return false;
      }
      if (line.unit_price === "" || parseFloat(line.unit_price) < 0) {
        toast.error("Every line needs a valid unit price");
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const submitData = {
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        customer_address: formData.customer_address,
        currency: formData.currency,
        tax_rate: taxRateNum,
        due_date: formData.due_date,
        notes: formData.notes,
        items: items.map(line => ({
          service_type: line.service_type,
          service_id: parseInt(line.service_id),
          service_code: line.service_code || undefined,
          description: line.description,
          unit: line.unit || "service",
          quantity: parseFloat(line.quantity),
          unit_price: parseFloat(line.unit_price),
        })),
      };

      const response = await axios.post(`${API_URL}/invoices`, submitData, config_token);

      if (response.data.status) {
        toast.success("Invoice created successfully!");
        navigate("/admin/list-invoices");
      }
    } catch (error: any) {
      console.error("Error creating invoice:", error.response?.data || error.message);
      toast.error(error.response?.data?.message || "Error creating invoice.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      customer_name: "",
      customer_email: "",
      customer_phone: "",
      customer_address: "",
      currency: "OMR",
      tax_rate: "0",
      due_date: "",
      notes: ""
    });
    setItems([emptyLine()]);
  };

  return (
    <>
      <div
        style={{
          width: "100%",
          minHeight: "100vh",
          backgroundColor: colors.bgPage,
          padding: "20px",
          boxSizing: "border-box",
          color: colors.text,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "20% 60% 20%",
            gap: "16px",
            width: "100%",
            boxSizing: "border-box",
            alignItems: "start",
          }}
        >
          {/* Colonne Gauche - Summary */}
          <div style={{ display: "flex", flexDirection: "column", gap: "16px", position: "sticky", top: "20px" }}>
            <div
              style={{
                background: "#fff",
                border: `0.5px solid ${colors.borderLight}`,
                borderRadius: 12,
                padding: 16,
                display: "flex",
                flexDirection: "column",
                gap: 14,
              }}
            >
              <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0, color: colors.text }}>Invoice Summary</h2>
              <p style={{ fontSize: 12, color: colors.textMuted, margin: 0, lineHeight: 1.4 }}>
                Review details before creating.
              </p>

              <div style={{ display: "flex", flexDirection: "column", gap: 10, fontSize: 12, background: colors.bgSubtle, padding: 12, borderRadius: 8 }}>
                <div>
                  <span style={{ color: colors.textFaint }}>Customer:</span>
                  <div style={{ fontWeight: 600, color: colors.text }}>{formData.customer_name || "N/A"}</div>
                </div>
                <div>
                  <span style={{ color: colors.textFaint }}>Lines:</span>
                  <div style={{ fontWeight: 600, color: colors.text }}>
                    {items.filter(l => l.description).length || 0} item{items.length !== 1 ? "s" : ""}
                  </div>
                </div>
                <div style={{ borderTop: `1px solid ${colors.borderLight}`, paddingTop: 8, marginTop: 4 }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>Subtotal:</span>
                    <span>{formData.currency} {subtotal.toFixed(2)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>Tax ({taxRateNum}%):</span>
                    <span>{formData.currency} {taxAmount.toFixed(2)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, marginTop: 6, color: colors.primary }}>
                    <span>Total:</span>
                    <span>{formData.currency} {total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Colonne Centre - Formulaire */}
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

              <div style={{ background: "#fff", border: `0.5px solid ${colors.borderLight}`, borderRadius: 12, padding: 24 }}>
                <h1 style={{ fontSize: 18, fontWeight: 600, margin: "0 0 4px 0", color: colors.text }}>Create New Invoice</h1>
                <p style={{ fontSize: 12, color: colors.textMuted, margin: 0 }}>Fill out the details below to generate a new billing invoice.</p>
              </div>

              {/* Customer Information */}
              <div style={{ background: "#fff", border: `0.5px solid ${colors.borderLight}`, borderRadius: 12, padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
                <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: colors.text }}>Customer Information</h3>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  <div>
                    <label style={labelStyle}>Customer Name *</label>
                    <input type="text" name="customer_name" placeholder="Enter customer full name" value={formData.customer_name} onChange={handleInputChange} required style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Customer Email *</label>
                    <input type="email" name="customer_email" placeholder="customer@example.com" value={formData.customer_email} onChange={handleInputChange} required style={inputStyle} />
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  <div>
                    <label style={labelStyle}>Customer Phone</label>
                    <input type="tel" name="customer_phone" placeholder="+1 234 567 8900" value={formData.customer_phone} onChange={handleInputChange} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Currency *</label>
                    <select name="currency" value={formData.currency} onChange={handleInputChange} required style={{ ...inputStyle, cursor: 'pointer', backgroundColor: '#fff' }}>
                      {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>Customer Address</label>
                  <textarea name="customer_address" placeholder="Enter customer full address" value={formData.customer_address} onChange={handleInputChange} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
                </div>

                <div>
                  <label style={labelStyle}>Due Date *</label>
                  <input type="date" name="due_date" value={formData.due_date} onChange={handleInputChange} required min={new Date().toISOString().split('T')[0]} style={{ ...inputStyle, maxWidth: 220 }} />
                </div>
              </div>

              {/* Invoice Lines */}
              <div style={{ background: "#fff", border: `0.5px solid ${colors.borderLight}`, borderRadius: 12, padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: colors.text }}>Invoice Lines</h3>
                  {isLoadingServices && <span style={{ fontSize: 11, color: colors.textFaint }}>Loading services...</span>}
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {items.map((line, index) => {
                    const options = filteredServicesFor(line.service_type);
                    const amount = lineAmount(line);
                    return (
                      <div
                        key={line.key}
                        style={{
                          border: `1px solid ${colors.borderLight}`,
                          borderRadius: 10,
                          padding: 14,
                          background: colors.bgSubtle,
                          display: "flex",
                          flexDirection: "column",
                          gap: 10,
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: colors.textFaint, letterSpacing: "0.04em" }}>LINE {index + 1}</span>
                          {items.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeLine(line.key)}
                              style={{ background: "none", border: "none", color: colors.danger, fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0 }}
                            >
                              Remove
                            </button>
                          )}
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                          <div>
                            <label style={labelStyle}>Service Type *</label>
                            <select
                              value={line.service_type}
                              onChange={(e) => updateLine(line.key, "service_type", e.target.value)}
                              style={{ ...inputStyle, cursor: 'pointer', backgroundColor: '#fff' }}
                            >
                              <option value="">Select Service Type</option>
                              <option value="visa_pack">Visa Pack</option>
                              <option value="trip_booking">Trip Booking</option>
                              <option value="rental_car">Rental Car</option>
                            </select>
                          </div>
                          <div>
                            <label style={labelStyle}>Service *</label>
                            <select
                              value={line.service_id}
                              onChange={(e) => updateLine(line.key, "service_id", e.target.value)}
                              disabled={!line.service_type || isLoadingServices}
                              style={{ ...inputStyle, cursor: 'pointer', backgroundColor: '#fff' }}
                            >
                              <option value="">Select Service</option>
                              {options.map(s => (
                                <option key={s.id} value={s.id}>
                                  {s.name} {s.price !== undefined && `- ${s.currency || formData.currency} ${s.price}`}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div>
                          <label style={labelStyle}>Description</label>
                          <input
                            type="text"
                            value={line.description}
                            onChange={(e) => updateLine(line.key, "description", e.target.value)}
                            placeholder="Auto-filled from the selected service, editable"
                            style={inputStyle}
                          />
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                          <div>
                            <label style={labelStyle}>Quantity *</label>
                            <input
                              type="number" min="0.01" step="0.01"
                              value={line.quantity}
                              onChange={(e) => updateLine(line.key, "quantity", e.target.value)}
                              style={inputStyle}
                            />
                          </div>
                          <div>
                            <label style={labelStyle}>Unit Price *</label>
                            <input
                              type="number" min="0" step="0.001"
                              value={line.unit_price}
                              onChange={(e) => updateLine(line.key, "unit_price", e.target.value)}
                              style={inputStyle}
                            />
                          </div>
                          <div>
                            <label style={labelStyle}>Line Amount</label>
                            <input type="text" readOnly value={`${formData.currency} ${amount.toFixed(2)}`} style={{ ...inputStyle, fontWeight: 600, color: colors.primary }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <button
                  type="button"
                  onClick={addLine}
                  style={{
                    alignSelf: "flex-start",
                    padding: "8px 16px",
                    borderRadius: 8,
                    border: `1px dashed ${colors.primary}`,
                    background: "#fff",
                    color: colors.primary,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  + Add Line
                </button>
              </div>

              {/* Tax */}
              <div style={{ background: "#fff", border: `0.5px solid ${colors.borderLight}`, borderRadius: 12, padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
                <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: colors.text }}>Tax</h3>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
                  <div>
                    <label style={labelStyle}>Tax Rate (%)</label>
                    <select name="tax_rate" value={formData.tax_rate} onChange={handleInputChange} style={{ ...inputStyle, cursor: 'pointer', backgroundColor: '#fff' }}>
                      <option value="0">No Tax (0%)</option>
                      <option value="5">Standard (5%)</option>
                      <option value="10">Reduced (10%)</option>
                      <option value="15">Premium (15%)</option>
                      <option value="20">High (20%)</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Subtotal</label>
                    <input type="text" readOnly value={`${formData.currency} ${subtotal.toFixed(2)}`} style={{ ...inputStyle, backgroundColor: colors.bgSubtle }} />
                  </div>
                  <div>
                    <label style={labelStyle}>Total</label>
                    <input type="text" readOnly value={`${formData.currency} ${total.toFixed(2)}`} style={{ ...inputStyle, backgroundColor: colors.bgSubtle, fontWeight: 700, color: colors.primary }} />
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div style={{ background: "#fff", border: `0.5px solid ${colors.borderLight}`, borderRadius: 12, padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
                <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: colors.text }}>Additional Information</h3>
                <div>
                  <label style={labelStyle}>Notes</label>
                  <textarea name="notes" placeholder="Additional notes or terms and conditions..." value={formData.notes} onChange={handleInputChange} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
                </div>
              </div>

              {/* Actions */}
              <div style={{ background: "#fff", border: `0.5px solid ${colors.borderLight}`, borderRadius: 12, padding: 16, display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button type="button" onClick={handleReset} style={{ padding: "8px 16px", backgroundColor: colors.bgSubtle, color: colors.text, border: `1px solid ${colors.border}`, borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 500 }}>
                  Reset
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/admin/list-invoices")}
                  style={{ padding: "8px 16px", backgroundColor: colors.cancelBg, color: "#fff", borderRadius: 8, border: "none", display: 'flex', alignItems: 'center', fontSize: 12, fontWeight: 500, cursor: "pointer" }}
                >
                  Cancel
                </button>
                <button type="submit" disabled={isLoading} style={{ padding: "8px 20px", backgroundColor: colors.primary, color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 500 }}>
                  {isLoading ? "Creating..." : "Create Invoice"}
                </button>
              </div>

            </form>
          </div>

          {/* Colonne Droite - Quick Actions */}
          <div style={{ display: "flex", flexDirection: "column", gap: "16px", position: "sticky", top: "20px" }}>
            <div style={{ background: "#fff", border: `0.5px solid ${colors.borderLight}`, borderRadius: 12, padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0, color: colors.text }}>Quick Actions</h3>
              <p style={{ fontSize: 12, color: colors.textMuted, margin: 0 }}>Return safely to the invoices list.</p>
              <button
                type="button"
                onClick={() => navigate("/admin/list-invoices")}
                style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: `1px solid ${colors.border}`, background: "#fff", color: colors.text, fontSize: 12, fontWeight: 500, cursor: "pointer" }}
              >
                Back to Invoices
              </button>
            </div>
          </div>

        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default AddInvoice;